import {
  AGENT_PAYOUT_COUNTRIES, agentPayoutConfirmLabel, agentPayoutWarning,
  chooseAgentPayoutCountry, confirmAgentPayoutCountry,
  type AgentPayoutChoice, type AgentPayoutSubject,
} from '@/lib/agentPayoutCountry';
import { payoutCountryName, payoutSetupCopy } from '@/lib/payoutSetupCopy';
import { PayoutSupportMessage } from './PayoutSupportMessage';
import formStyles from '@/components/ui/Form.module.css';
import styles from './AgentPayoutCountryField.module.css';

/**
 * The bank account country for a Sales Partner's NEW Stripe Express
 * account (item 6, 2026-10-03), on the host pattern (LocationBankCard,
 * the driver app's PayoutCountryField): a select that starts empty
 * ("Choose country", then United States and Canada), the cannot-change
 * warning, and a confirmation ticked once a country is chosen. Rendered
 * only before an account exists; the caller gates its Stripe button on
 * canStartAgentConnect and sends agentConnectStartBody.
 *
 * Changing the country clears the confirmation, so a tick given for one
 * country never carries over to the other. `updating` (the Api does not
 * take a country yet, agentApiTakesCountry) adds a line saying why the
 * Stripe button waits.
 *
 * `locale` picks the copy (payoutSetupCopy). The Sales Partner's own dialog
 * passes payoutLocale(); the Agents edit modal leaves it out and stays in
 * English like the rest of that modal.
 */
export interface AgentPayoutCountryFieldProps {
  /** Distinct per screen, for the select and checkbox ids. */
  idPrefix: string;
  /** 'self' on the Sales Partner's own dialog, 'partner' for an admin. */
  subject: AgentPayoutSubject;
  value: AgentPayoutChoice;
  onChange: (next: AgentPayoutChoice) => void;
  /** True while the Api would ignore the country (agentApiTakesCountry). */
  updating?: boolean;
  disabled?: boolean;
  /** The copy language; English when left out. */
  locale?: string;
}

export function AgentPayoutCountryField({
  idPrefix, subject, value, onChange, updating = false, disabled = false, locale = 'en',
}: AgentPayoutCountryFieldProps) {
  const text = payoutSetupCopy(locale);
  const selectId = `${idPrefix}-payout-country`;
  const confirmId = `${idPrefix}-payout-confirm`;
  return (
    <div className={styles.fields}>
      <div className={formStyles.field}>
        <label htmlFor={selectId} className={formStyles.label}>{text.bankAccountCountry}</label>
        <select
          id={selectId}
          className={formStyles.select}
          value={value.country}
          onChange={(e) => onChange(chooseAgentPayoutCountry(e.target.value))}
          disabled={disabled}
        >
          <option value="">{text.chooseCountry}</option>
          {AGENT_PAYOUT_COUNTRIES.map((c) => (
            <option key={c} value={c}>{payoutCountryName(text, c)}</option>
          ))}
        </select>
        <p className={styles.warning}><PayoutSupportMessage text={agentPayoutWarning(subject, locale)} locale={locale} /></p>
      </div>

      {value.country !== '' && (
        <label className={`${styles.confirmRow} ${styles.reveal}`} htmlFor={confirmId}>
          <input
            id={confirmId}
            type="checkbox"
            checked={value.confirmed}
            onChange={(e) => onChange(confirmAgentPayoutCountry(value, e.target.checked))}
            disabled={disabled}
          />
          <span>{agentPayoutConfirmLabel(subject, value.country, locale)}</span>
        </label>
      )}

      {updating && (
        <p className={styles.warning} role="status">{text.agentUpdating}</p>
      )}
    </div>
  );
}
